import {
  Examination_Type,
  Trial_File_Type,
  Trial_Status,
  TrialHeaderQuery,
  useCancelTrialMutation,
} from "@generated/graphql";
import { LoadingButton } from "@mui/lab";
import { Checkbox, FormControlLabel, Stack, Tooltip, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import ExaminationTypeTitle from "components/ExaminationTypeTitle";
import TestingStatus from "components/TestingStatus";
import { TESTING_TYPES_DATA } from "constants/global";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import { parseApolloError } from "helpers/errorsParser";
import useGenerateTrialFile from "hooks/useGenerateTrialFile";
import useIsCreate from "hooks/useIsCreate";
import useSetTabTitle from "hooks/useSetTabTitle";
import { useContext, useState } from "react";
import { useParams } from "react-router-dom";
import CancelExaminationButton from "../../components/CancelExaminationButton";
import { getTestingTitle } from "../../helpers";

interface PersonalHeaderProps {
  trial: TrialHeaderQuery["trial"] | undefined;
}

const PersonalHeader = ({ trial }: PersonalHeaderProps) => {
  const { addToast } = useContext(ToastContext);
  const [isReportReduced, setIsReportReduced] = useState<boolean>(false);
  const isCreate = useIsCreate();
  const { id } = useParams();
  const { id: trialId = id, status, examination } = trial || {};

  const isCanCancel = status === Trial_Status.Created;

  const [cancelTrial, { loading: cancelTrialLoading }] = useCancelTrialMutation();

  const handleCancelExamination = async () => {
    cancelTrial({
      variables: {
        trialId: id!,
      },
    })
      .then(() => addToast({ type: ToastTypeEnum.SUCCESS }))
      .catch((e) => {
        parseApolloError(e, { addToast });
      });
  };

  const { numericId } = examination || {};
  const title = getTestingTitle(examination, examination?.type === Examination_Type.Personal ? undefined : trial);
  useSetTabTitle(title, numericId);

  const { onGenerateTrialFile, loading } = useGenerateTrialFile();

  const isDownloadReportDisabled = status !== Trial_Status.Completed && !trial?.isAssessmentComplete;

  const examinationTypeData = TESTING_TYPES_DATA.find((data) => data.type === Examination_Type.Personal);

  const downloadReportType: Trial_File_Type = isReportReduced
    ? Trial_File_Type.CandidateReport
    : Trial_File_Type.RecruiterReport;

  const handleGenerateTrialFile = () => {
    if (trialId) onGenerateTrialFile(trialId, downloadReportType);
    else addToast({ type: ToastTypeEnum.ERROR, text: "Отсутствует id тестирования" });
  };

  return (
    <Stack
      direction='row'
      justifyContent='space-between'
      mb={2.5}
      spacing={2}>
      <Stack
        direction='row'
        alignItems='flex-start'
        spacing={2}>
        <Typography
          variant='h28'
          sx={{ wordBreak: "normal" }}>
          {title}
        </Typography>
        <ExaminationTypeTitle examinationTypeData={examinationTypeData} />
      </Stack>
      <Stack
        direction='row'
        spacing={2}
        alignItems='flex-start'>
        {!isCreate && (
          <>
            {status && <TestingStatus status={status} />}
            <CancelExaminationButton
              buttonProps={{ disabled: !isCanCancel, loading: cancelTrialLoading }}
              handleCancelExamination={handleCancelExamination}
            />
          </>
        )}
        {!isCreate && (
          <Stack spacing={1}>
            <Tooltip title={isDownloadReportDisabled ? "Отчет еще не сформирован" : undefined}>
              <Box>
                <LoadingButton
                  variant='contained'
                  color='primary'
                  loading={loading}
                  disabled={isDownloadReportDisabled}
                  sx={{ gap: "6px", whiteSpace: "nowrap" }}
                  onClick={handleGenerateTrialFile}>
                  Выгрузить отчет
                </LoadingButton>
              </Box>
            </Tooltip>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isReportReduced}
                  onChange={(event) => setIsReportReduced(event.target.checked)}
                  inputProps={{ "aria-label": "controlled" }}
                />
              }
              label={<Typography sx={{ whiteSpace: "nowrap" }}> Сокращенный отчет</Typography>}
            />
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default PersonalHeader;
